'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSession } from 'next-auth/react';
import { FileText } from 'lucide-react';
import { Container } from '@/components/utils/Container';
import { cn } from '@/lib/utils';
import { SearchHero } from './components/SearchHero';
import { SearchComposer, type AttachedResumeInfo } from './components/SearchComposer';
import { ResultsFeed } from './components/ResultsFeed';
import { ResumeIntakeDialog } from '@/components/features/resume/ResumeIntakeDialog';
import type { IntakeHandlerResult, IntakeProgressReporter, IntakeResume } from '@/components/features/resume/ResumeIntakePanel';
import {
  MOCK_CLASSIFICATION,
  MOCK_CONTACTS_COMMENTARY,
  MOCK_INSIGHTS,
  MOCK_JOBS_COMMENTARY,
  MOCK_MORE_CONTACTS_INITIAL,
  MOCK_MORE_CONTACTS_TOTAL,
  MOCK_MORE_JOBS_INITIAL,
  MOCK_MORE_JOBS_TOTAL,
  MOCK_TOP_MATCHES,
  mockPagers,
} from './components/mocks';
import {
  ClassifiedQuery,
  FeedItem,
  INITIAL_STAGES,
  FilterType,
  ResultFilter,
  SearchFilterGroup,
  SearchLogEntry,
  SearchRound,
} from './types';
import type { SourceAnalysis } from '@/lib/report-generation/steps/analyze-sources';
import { createSearchStream } from '@/lib/unified-search/stream';
import type { SearchStreamEvent } from '@/lib/unified-search/types';

function analysisToClassifiedQuery(raw: string, analysis: SourceAnalysis): ClassifiedQuery {
  const industries = Array.isArray(analysis.industries)
    ? analysis.industries
        .map((industry) => (typeof industry === 'string' ? industry : industry?.name))
        .filter((value): value is string => Boolean(value))
    : [];
  const seniority =
    Array.isArray(analysis.seniority) && analysis.seniority.length > 0
      ? analysis.seniority
      : Array.isArray(analysis.specializations)
        ? analysis.specializations
        : [];
  const validIntents = ['jobs', 'companies', 'contacts'] as const;
  const intent = validIntents.includes(analysis.intent as any)
    ? (analysis.intent as typeof validIntents[number])
    : 'companies';

  return {
    raw,
    intent,
    companies: Array.isArray(analysis.companies) ? analysis.companies : [],
    roles: Array.isArray(analysis.roles) ? analysis.roles : [],
    locations: Array.isArray(analysis.locations) ? analysis.locations : [],
    industries,
    seniority,
    similarCompanies: Array.isArray(analysis.similarCompanies) ? analysis.similarCompanies : [],
    similarTitles: Array.isArray(analysis.similarTitles) ? analysis.similarTitles : [],
    similarSkills: Array.isArray(analysis.similarSkills) ? analysis.similarSkills : [],
  };
}

/**
 * Adapter that maps transport-level SearchStreamEvents onto SearchRound
 * mutations. Preserves the exact apply / onClassified / onDone semantics that
 * previously lived inside the local startSearchStream implementation.
 */
function createStreamEventHandler(
  apply: (mut: (prev: SearchRound) => SearchRound) => void,
  onClassified?: (analysis: SourceAnalysis) => void,
  onDone?: () => void,
): (event: SearchStreamEvent) => void {
  return (event) => {
    if (event.type === 'classified') {
      const analysis = event.analysis;
      apply((prev) => ({
        ...prev,
        classified: analysisToClassifiedQuery(prev.query, analysis),
      }));
      onClassified?.(analysis);
    } else if (event.type === 'stage') {
      const key = event.key;
      const status = event.status;
      apply((prev) => {
        const next = { ...prev, stages: { ...prev.stages, [key]: status } };
        // When the answer stage completes, stop the streaming cursor
        if (key === 'answer' && status === 'completed') {
          next.feed = next.feed.map((item) =>
            item.type === 'answerSection' ? { ...item, isStreaming: false } : item,
          );
        }
        return next;
      });
    } else if (event.type === 'answer-chunk') {
      const chunk = event.text;
      apply((prev) => {
        const existing = prev.feed.find((item) => item.type === 'answerSection');
        if (existing?.type === 'answerSection') {
          return {
            ...prev,
            feed: prev.feed.map((item) =>
              item.type === 'answerSection'
                ? { ...item, answer: item.answer + chunk, isStreaming: true }
                : item,
            ),
          };
        }
        return {
          ...prev,
          feed: [
            ...prev.feed,
            {
              type: 'answerSection' as const,
              id: 'answer',
              question: prev.query,
              answer: chunk,
              isStreaming: true,
            },
          ],
        };
      });
    } else if (event.type === 'partial') {
      const items = event.items;
      apply((prev) => {
        const existingIds = new Set(prev.feed.map((f) => f.id));
        const newItems = items.filter((item) => !existingIds.has(item.id));
        if (newItems.length === 0) return prev;
        return { ...prev, feed: [...prev.feed, ...newItems] };
      });
    } else if (event.type === 'log') {
      const logEntry: SearchLogEntry = {
        id: `log-${event.key}-${Date.now()}`,
        stageKey: event.key,
        message: event.message,
        details: event.details,
      };
      apply((prev) => ({
        ...prev,
        logs: [...prev.logs, logEntry],
      }));
    } else if (event.type === 'timing') {
      const secs = (event.durationMs / 1000).toFixed(2);
      const style = event.durationMs > 5000 ? 'color: red; font-weight: bold' : event.durationMs > 2000 ? 'color: orange' : 'color: green';
      console.log(`%c⏱ ${event.label}: ${secs}s${event.phase ? ` [${event.phase}]` : ''}`, style);
    } else if (event.type === 'error') {
      apply((prev) => ({ ...prev, errored: true }));
    } else if (event.type === 'done') {
      onDone?.();
    }
  };
}

const MAX_AUTO_VALUES_PER_GROUP = 5;

/** Replace autoValues for each type returned by the parse API (non-additive). */
function applyAutoValues(
  existing: SearchFilterGroup[],
  parsed: Array<{ type: FilterType; values: string[] }>,
): SearchFilterGroup[] {
  const result = existing.map((g) => ({ ...g, autoValues: [] as string[] }));
  for (const p of parsed) {
    const eg = result.find((g) => g.type === p.type);
    if (eg) {
      eg.autoValues = p.values.slice(0, MAX_AUTO_VALUES_PER_GROUP);
    } else if (p.values.length > 0) {
      result.push({ type: p.type, values: [], autoValues: p.values.slice(0, MAX_AUTO_VALUES_PER_GROUP) });
    }
  }
  return result;
}

/** Merge similar values from the classified event into existing autoValues (additive, no duplicates). */
function mergeSimilarAutoValues(
  existing: SearchFilterGroup[],
  similar: Array<{ type: FilterType; values: string[] }>,
): SearchFilterGroup[] {
  const result = existing.map((g) => ({ ...g }));
  for (const s of similar) {
    const eg = result.find((g) => g.type === s.type);
    if (eg) {
      const combined = new Set([...eg.autoValues, ...s.values]);
      eg.values.forEach((v) => combined.delete(v));
      eg.autoValues = [...combined].slice(0, MAX_AUTO_VALUES_PER_GROUP);
    } else if (s.values.length > 0) {
      result.push({ type: s.type, values: [], autoValues: s.values.slice(0, MAX_AUTO_VALUES_PER_GROUP) });
    }
  }
  return result;
}

/** Strip all auto-detected values, keep manually added ones. */
function clearAutoValues(groups: SearchFilterGroup[]): SearchFilterGroup[] {
  return groups.map((g) => ({ ...g, autoValues: [] })).filter((g) => g.values.length > 0);
}

function _simulateSearchStream(
  apply: (mut: (prev: SearchRound) => SearchRound) => void,
): () => void {
  const timers: ReturnType<typeof setTimeout>[] = [];
  const at = (ms: number, fn: () => void) => {
    timers.push(setTimeout(fn, ms));
  };

  apply((prev) => ({
    ...prev,
    stages: { ...prev.stages, understanding: 'running' },
  }));

  at(700, () => {
    apply((prev) => ({
      ...prev,
      classified: MOCK_CLASSIFICATION,
      stages: {
        ...prev.stages,
        understanding: 'completed',
        exactMatch: 'completed',
        similarExpansion: 'running',
        companies: 'running',
        jobs: 'running',
        contacts: 'running',
      },
      logs: [
        ...prev.logs,
        {
          id: 'log-understanding',
          stageKey: 'understanding' as const,
          message: 'Parsed your query into 3 search dimensions',
          details: [
            `Companies: ${MOCK_CLASSIFICATION.companies.join(', ') || '(none)'}`,
            `Roles: ${MOCK_CLASSIFICATION.roles.join(', ')}`,
            `Locations: ${MOCK_CLASSIFICATION.locations.join(', ')}`,
          ],
        },
      ],
      feed: [
        ...prev.feed,
        {
          type: 'insight',
          id: 'insight-top',
          placement: 'top',
          text: MOCK_INSIGHTS.top,
        },
      ],
    }));
  });

  at(1100, () => {
    apply((prev) => ({
      ...prev,
      stages: { ...prev.stages, similarExpansion: 'completed' },
      logs: [
        ...prev.logs,
        {
          id: 'log-similar',
          stageKey: 'similarExpansion' as const,
          message: `Found ${MOCK_TOP_MATCHES.length} similar companies`,
          details: MOCK_TOP_MATCHES.map((m) => m.company.name),
        },
      ],
      feed: [
        ...prev.feed,
        {
          type: 'similarCompaniesSection',
          id: 'similar-companies',
          companyNames: MOCK_TOP_MATCHES.map((m) => m.company.name),
        },
      ],
    }));
  });

  at(1300, () => {
    apply((prev) => ({
      ...prev,
      stages: { ...prev.stages, companies: 'completed' },
      logs: [
        ...prev.logs,
        {
          id: 'log-companies',
          stageKey: 'companies' as const,
          message: `Contacts loaded for ${MOCK_TOP_MATCHES.length} companies`,
        },
      ],
    }));
  });

  at(1900, () => {
    apply((prev) => ({
      ...prev,
      stages: { ...prev.stages, jobs: 'completed' },
      logs: [
        ...prev.logs,
        {
          id: 'log-jobs',
          stageKey: 'jobs' as const,
          message: `Found ${MOCK_MORE_JOBS_TOTAL} open positions`,
          details: ['Titles: Product Manager, Senior Engineer', 'Locations: San Francisco, New York'],
        },
      ],
      feed: [
        ...prev.feed,
        {
          type: 'jobsSection',
          id: 'jobs',
          jobs: MOCK_MORE_JOBS_INITIAL,
          total: MOCK_MORE_JOBS_TOTAL,
          commentary: MOCK_JOBS_COMMENTARY,
        },
      ],
    }));
  });

  at(2500, () => {
    apply((prev) => ({
      ...prev,
      stages: { ...prev.stages, contacts: 'completed' },
      logs: [
        ...prev.logs,
        {
          id: 'log-contacts',
          stageKey: 'contacts' as const,
          message: `Found ${MOCK_MORE_CONTACTS_TOTAL} additional contacts outside top companies`,
        },
      ],
      feed: [
        ...prev.feed,
        {
          type: 'contactsSection',
          id: 'contacts',
          contacts: MOCK_MORE_CONTACTS_INITIAL,
          total: MOCK_MORE_CONTACTS_TOTAL,
          commentary: MOCK_CONTACTS_COMMENTARY,
        },
        {
          type: 'insight',
          id: 'insight-bottom',
          placement: 'bottom',
          text: MOCK_INSIGHTS.bottom,
        },
      ],
    }));
  });

  return () => {
    timers.forEach(clearTimeout);
  };
}

/* -------------------------------- Helpers -------------------------------- */

let roundCounter = 0;

function createEmptyRound(query: string, resumeTitle?: string | null): SearchRound {
  return {
    id: `round-${++roundCounter}-${Date.now()}`,
    query,
    resumeTitle: resumeTitle ?? null,
    classified: null,
    stages: { ...INITIAL_STAGES },
    feed: [],
    logs: [],
    errored: false,
  };
}

/* -------------------------------- Resume --------------------------------- */

async function fetchResumeText(resumeId: string, sourceType: 'uploaded' | 'builder'): Promise<string | null> {
  try {
    if (sourceType === 'uploaded') {
      const res = await fetch(`/api/settings/resumes/${resumeId}`, { cache: 'no-store' });
      if (!res.ok) return null;
      const data = await res.json();
      return data.contentText || null;
    }
    // Builder resumes — fetch structured content and extract text
    const res = await fetch(`/api/resume-builder/${resumeId}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    const content = data.resume?.content;
    if (!content) return null;
    // Build a plain-text summary from structured builder content
    const parts: string[] = [];
    if (content.name) parts.push(content.name);
    if (content.title) parts.push(content.title);
    if (content.summary) parts.push(content.summary);
    if (Array.isArray(content.experience)) {
      for (const exp of content.experience) {
        const line = [exp.title, exp.company, exp.location].filter(Boolean).join(' - ');
        if (line) parts.push(line);
        if (exp.description) parts.push(exp.description);
      }
    }
    if (Array.isArray(content.education)) {
      for (const edu of content.education) {
        const line = [edu.degree, edu.institution].filter(Boolean).join(' - ');
        if (line) parts.push(line);
      }
    }
    if (Array.isArray(content.skills)) {
      parts.push(`Skills: ${content.skills.join(', ')}`);
    }
    return parts.length > 0 ? parts.join('\n') : null;
  } catch {
    return null;
  }
}

/* -------------------------------- Component ------------------------------ */

interface SearchClientProps {
  initialQuery?: string;
  initialSearchId?: string | null;
  initialResumeId?: string | null;
  autoSave?: boolean;
}

export default function SearchClient({ initialQuery, initialSearchId, initialResumeId, autoSave }: SearchClientProps) {
  const { status: authStatus } = useSession();
  const [draft, setDraft] = useState((initialQuery ?? '').trim());
  const [filterGroups, setFilterGroups] = useState<SearchFilterGroup[]>([]);
  const [rounds, setRounds] = useState<SearchRound[]>([]);
  const [isHydrating, setIsHydrating] = useState(!!initialSearchId);
  const [needsResume, setNeedsResume] = useState(false);
  const [resultFilter, setResultFilter] = useState<ResultFilter>('all');
  const [refinement, setRefinement] = useState('');
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerMode, setComposerMode] = useState<'refine' | 'edit'>('refine');
  const [attachedResume, setAttachedResume] = useState<AttachedResumeInfo | null>(null);
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
  const cancelRef = useRef<(() => void) | null>(null);
  const lastAutoSubmittedQueryRef = useRef('');
  const roundsRef = useRef<SearchRound[]>([]);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inlineRefineRef = useRef<HTMLDivElement>(null);
  const [inlineRefineVisible, setInlineRefineVisible] = useState(false);
  const searchIdRef = useRef<string | null>(initialSearchId ?? null);
  const snapshotTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSnapshotTimeRef = useRef<number>(0);
  const pendingSnapshotRef = useRef<SearchRound | null>(null);

  useEffect(() => { roundsRef.current = rounds; }, [rounds]);
  useEffect(() => () => {
    cancelRef.current?.();
    if (snapshotTimerRef.current) clearTimeout(snapshotTimerRef.current);
  }, []);

  useEffect(() => {
    const el = inlineRefineRef.current;
    if (!el) { setInlineRefineVisible(false); return; }
    const observer = new IntersectionObserver(
      ([entry]) => setInlineRefineVisible(entry.isIntersecting),
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [rounds]);

  const flushSnapshot = useCallback((round: SearchRound) => {
    const sid = searchIdRef.current;
    if (!sid) return;
    lastSnapshotTimeRef.current = Date.now();
    pendingSnapshotRef.current = null;
    fetch(`/api/search/${sid}/snapshot`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: round.query,
        classified: round.classified,
        stages: round.stages,
        feed: round.feed,
      }),
    }).catch(() => {});
  }, []);

  const pushSnapshot = useCallback((round: SearchRound) => {
    if (!searchIdRef.current) return;
    pendingSnapshotRef.current = round;

    const elapsed = Date.now() - lastSnapshotTimeRef.current;
    if (elapsed >= 3000) {
      // Throttle window passed — fire immediately
      if (snapshotTimerRef.current) clearTimeout(snapshotTimerRef.current);
      flushSnapshot(round);
    } else {
      // Schedule a trailing flush at end of throttle window
      if (!snapshotTimerRef.current) {
        snapshotTimerRef.current = setTimeout(() => {
          snapshotTimerRef.current = null;
          const pending = pendingSnapshotRef.current;
          if (pending) flushSnapshot(pending);
        }, 3000 - elapsed);
      }
    }
  }, [flushSnapshot]);

  useEffect(() => {
    if (!initialResumeId || attachedResume) return;
    (async () => {
      const text = await fetchResumeText(initialResumeId, 'uploaded');
      if (text) {
        setAttachedResume({ id: initialResumeId, title: 'Resume', contentText: text });
      } else {
        const builderText = await fetchResumeText(initialResumeId, 'builder');
        if (builderText) {
          setAttachedResume({ id: initialResumeId, title: 'Resume', contentText: builderText });
        }
      }
    })();
  }, [initialResumeId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const text = draft.trim();

    if (text.length < 3) {
      const rafId = window.requestAnimationFrame(() => {
        setFilterGroups((prev) => clearAutoValues(prev));
      });
      return () => window.cancelAnimationFrame(rafId);
    }

    const timer = window.setTimeout(async () => {
      try {
        const res = await fetch('/api/search/parse', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text }),
        });
        if (!res.ok) return;
        const data = await res.json();
        const parsed = [
          { type: 'location' as FilterType, values: data.locations ?? [] },
          { type: 'skill' as FilterType, values: data.skills ?? [] },
          { type: 'title' as FilterType, values: data.titles ?? [] },
          { type: 'company' as FilterType, values: data.companies ?? [] },
        ];
        setFilterGroups((prev) => {
          let groups = applyAutoValues(prev, parsed);
          const similar: Array<{ type: FilterType; values: string[] }> = [];
          if (data.similarCompanies?.length) similar.push({ type: 'company', values: data.similarCompanies });
          if (data.similarTitles?.length) similar.push({ type: 'title', values: data.similarTitles });
          if (data.similarSkills?.length) similar.push({ type: 'skill', values: data.similarSkills });
          if (similar.length > 0) groups = mergeSimilarAutoValues(groups, similar);
          return groups;
        });
      } catch {
        // silently ignore parse failures
      }
    }, 900);

    return () => window.clearTimeout(timer);
  }, [draft]);

  useEffect(() => {
    if (!initialSearchId) return;
    let cancelled = false;

    fetch(`/api/search/${initialSearchId}`)
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (cancelled) return;
        if (!data?.content) {
          setIsHydrating(false);
          return;
        }
        const { query, classified, feed } = data.content as {
          query: string;
          classified: ClassifiedQuery | null;
          feed: FeedItem[];
        };
        const allCompleted = {
          understanding: 'completed' as const,
          exactMatch: 'completed' as const,
          similarExpansion: 'completed' as const,
          companies: 'completed' as const,
          jobs: 'completed' as const,
          expandedJobs: 'completed' as const,
          contacts: 'completed' as const,
          answer: 'completed' as const,
        };
        const rawStages = data.stages ?? allCompleted;
        const restoredStages = Object.fromEntries(
          Object.entries(rawStages).map(([k, v]) => [k, (v === 'running' || v === 'pending') ? 'completed' : v])
        ) as typeof allCompleted;
        // Synthesize logs from feed so UnifiedStep renders each section
        const logs: SearchLogEntry[] = [];
        if (classified) {
          const details: string[] = [];
          if (classified.roles.length) details.push(`Roles: ${classified.roles.join(', ')}`);
          if (classified.locations.length) details.push(`Locations: ${classified.locations.join(', ')}`);
          if (classified.companies.length) details.push(`Companies: ${classified.companies.join(', ')}`);
          logs.push({ id: 'restored-understanding', stageKey: 'understanding', message: 'Parsed your query', details });
        }
        const similar = feed.find((f: FeedItem) => f.type === 'similarCompaniesSection');
        if (similar?.type === 'similarCompaniesSection') {
          logs.push({ id: 'restored-similar', stageKey: 'similarExpansion', message: `Found ${similar.companyNames.length} similar companies`, details: similar.companyNames });
        }
        const topMatches = feed.find((f: FeedItem) => f.type === 'topMatchesSection');
        if (topMatches?.type === 'topMatchesSection' && topMatches.matches.length > 0) {
          logs.push({ id: 'restored-companies', stageKey: 'companies', message: `Contacts loaded for ${topMatches.matches.length} companies` });
        }
        const jobs = feed.find((f: FeedItem) => f.type === 'jobsSection' && f.id !== 'expanded-jobs');
        if (jobs?.type === 'jobsSection') {
          logs.push({ id: 'restored-jobs', stageKey: 'jobs', message: `Found ${jobs.total} open positions` });
        }
        const expandedJobs = feed.find((f: FeedItem) => f.type === 'jobsSection' && f.id === 'expanded-jobs');
        if (expandedJobs?.type === 'jobsSection') {
          logs.push({ id: 'restored-expanded-jobs', stageKey: 'expandedJobs', message: `Found ${expandedJobs.total} related positions` });
        }
        const contacts = feed.find((f: FeedItem) => f.type === 'contactsSection');
        if (contacts?.type === 'contactsSection') {
          logs.push({ id: 'restored-contacts', stageKey: 'contacts', message: `Found ${contacts.total} contacts` });
        }
        const answerItem = feed.find((f: FeedItem) => f.type === 'answerSection');
        if (answerItem?.type === 'answerSection') {
          logs.push({ id: 'restored-answer', stageKey: 'answer', message: 'Generated an answer' });
        }

        const isComplete = Object.values(rawStages).every(
          (s) => s === 'completed' || s === 'failed'
        );
        setDraft(query);
        setRounds([{
          id: `hydrated-${Date.now()}`,
          query,
          resumeTitle: null,
          classified,
          stages: restoredStages,
          feed,
          logs,
          errored: false,
        }]);
        setIsHydrating(false);
        if (!isComplete) setNeedsResume(true);
      })
      .catch(() => {
        if (!cancelled) setIsHydrating(false);
      });

    return () => { cancelled = true; };
  }, [initialSearchId]);

  const isSearching = useMemo(
    () => rounds.some((r) => Object.values(r.stages).some((s) => s === 'running')),
    [rounds],
  );
  const hasResults = rounds.length > 0;

  const applyToRound = useCallback((roundId: string) => {
    return (mut: (prev: SearchRound) => SearchRound) => {
      setRounds((prev) => {
        const next = prev.map((r) => r.id === roundId ? mut(r) : r);
        const updated = next.find((r) => r.id === roundId);
        if (updated) pushSnapshot(updated);
        return next;
      });
    };
  }, [pushSnapshot]);

  const launchRound = useCallback((round: SearchRound, context?: string[], resumeText?: string | null) => {
    cancelRef.current?.();
    const apply = applyToRound(round.id);

    cancelRef.current = createSearchStream(
      { query: round.query, context, resumeText },
      createStreamEventHandler(apply, (analysis) => {
        const similar: Array<{ type: FilterType; values: string[] }> = [];
        if (analysis.similarCompanies?.length) similar.push({ type: 'company', values: analysis.similarCompanies });
        if (analysis.similarTitles?.length) similar.push({ type: 'title', values: analysis.similarTitles });
        if (analysis.similarSkills?.length) similar.push({ type: 'skill', values: analysis.similarSkills });
        if (similar.length > 0) setFilterGroups((prev) => mergeSimilarAutoValues(prev, similar));
      }, () => {
        // Flush any pending debounced snapshot immediately
        if (snapshotTimerRef.current) {
          clearTimeout(snapshotTimerRef.current);
          snapshotTimerRef.current = null;
        }

        setRounds((current) => {
          const finished = current.find((r) => r.id === round.id);
          if (finished) {
            // Push final snapshot immediately (no debounce)
            const sid = searchIdRef.current;
            if (sid) {
              fetch(`/api/search/${sid}/snapshot`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  query: finished.query,
                  classified: finished.classified,
                  stages: finished.stages,
                  feed: finished.feed,
                }),
              }).catch(() => {});
            }

            // Run eval / autoSave promotion
            fetch('/api/search/evaluate', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                searchId: sid,
                query: finished.query,
                classified: finished.classified,
                feed: finished.feed,
                autoSave: autoSave || false,
              }),
            })
              .then((res) => res.ok ? res.json() : null)
              .then((data) => {
                if (data?.promoted && data.searchId && data.searchId !== sid) {
                  searchIdRef.current = data.searchId;
                  const url = new URL(window.location.href);
                  url.searchParams.set('searchId', data.searchId);
                  window.history.replaceState({}, '', url.toString());
                }
              })
              .catch(() => {});
          }
          return current;
        });
      }),
    );
  }, [applyToRound, autoSave]);

  useEffect(() => {
    if (!needsResume) return;
    setNeedsResume(false);
    const current = roundsRef.current;
    if (current.length === 0) return;
    const existingRound = current[current.length - 1];
    if (!existingRound.query) return;
    launchRound(existingRound);
  }, [needsResume, launchRound]);

  const submit = useCallback((rawQuery: string) => {
    const query = rawQuery.trim();
    if (!query && !attachedResume?.contentText) return;

    cancelRef.current?.();
    setResultFilter('all');

    // Generate searchId immediately, update URL
    const newSearchId = crypto.randomUUID();
    searchIdRef.current = newSearchId;
    const url = new URL(window.location.href);
    url.searchParams.set('searchId', newSearchId);
    window.history.replaceState({}, '', url.toString());

    // Fire-and-forget: init the cache entry server-side
    const effectiveQuery = query || 'Find jobs matching my resume';
    fetch('/api/search/init', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ searchId: newSearchId, query: effectiveQuery }),
    }).catch(() => {});

    const round = createEmptyRound(effectiveQuery, attachedResume?.title ?? null);
    setRounds([round]);
    launchRound(round, undefined, attachedResume?.contentText);
  }, [attachedResume?.contentText, attachedResume?.title, launchRound]);

  const refine = useCallback((rawQuery: string) => {
    const query = rawQuery.trim();
    if (!query) return;

    cancelRef.current?.();
    const context = roundsRef.current.map((r) => r.query);
    const round = createEmptyRound(query, attachedResume?.title ?? null);
    setRounds((prev) => [...prev, round]);
    launchRound(round, context, attachedResume?.contentText);
    setRefinement('');

    requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });
  }, [attachedResume?.contentText, attachedResume?.title, launchRound]);

  const reset = useCallback(() => {
    cancelRef.current?.();
    cancelRef.current = null;
    if (snapshotTimerRef.current) clearTimeout(snapshotTimerRef.current);
    searchIdRef.current = null;
    setDraft('');
    setFilterGroups([]);
    setResultFilter('all');
    setRounds([]);
    setRefinement('');
    setAttachedResume(null);
    const url = new URL(window.location.href);
    url.searchParams.delete('searchId');
    window.history.replaceState({}, '', url.toString());
  }, []);

  /* ---- Resume handlers ---- */

  const attachResume = useCallback(async (resumeId: string, title: string, sourceType: 'uploaded' | 'builder') => {
    const contentText = await fetchResumeText(resumeId, sourceType);
    if (!contentText) {
      throw new Error('Could not extract text from this resume. Try a different one or upload a new file.');
    }
    setAttachedResume({ id: resumeId, title, contentText });
  }, []);

  const handleResumeUpload = useCallback(async (
    file: File,
    progress: IntakeProgressReporter,
  ): Promise<IntakeHandlerResult> => {
    progress.setStages([
      { key: 'upload', label: 'Uploading file...' },
      { key: 'parse', label: 'Parsing resume content...' },
      { key: 'attach', label: 'Attaching to search...' },
    ]);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', file.name.replace(/\.[^/.]+$/, ''));
    const response = await fetch('/api/settings/resumes', {
      method: 'POST',
      body: formData,
    });
    progress.setStage('parse');
    const payload = (await response.json().catch(() => null)) as
      | { success?: boolean; sourceId?: string; error?: string }
      | null;
    if (!response.ok || !payload?.success) {
      throw new Error(payload?.error || 'Failed to upload resume');
    }
    progress.setStage('attach');
    const resumeId = payload.sourceId;
    if (!resumeId) {
      throw new Error('Upload succeeded but no resume ID was returned');
    }
    await attachResume(resumeId, file.name.replace(/\.[^/.]+$/, ''), 'uploaded');
    return { resumeId, shouldClose: true };
  }, [attachResume]);

  const handleResumeSelectExisting = useCallback(async (
    resume: IntakeResume,
    progress: IntakeProgressReporter,
  ): Promise<IntakeHandlerResult> => {
    progress.setStages([
      { key: 'attach', label: 'Attaching resume to search...' },
    ]);
    await attachResume(resume.id, resume.title, resume.sourceType);
    return { resumeId: resume.id, shouldClose: true };
  }, [attachResume]);

  const isAuthenticated = authStatus === 'authenticated';

  const handleAddResumeClick = useCallback(() => {
    setResumeDialogOpen(true);
  }, []);

  const handleEditQuery = useCallback((queryText: string) => {
    setRefinement(queryText);
    setComposerMode('edit');
    setComposerOpen(true);
  }, []);

  const handleDeleteRound = useCallback((roundId: string) => {
    setRounds((prev) => {
      const idx = prev.findIndex((r) => r.id === roundId);
      if (idx === -1) return prev;
      return prev.slice(0, idx);
    });
  }, []);

  const handleComposerSubmit = useCallback(() => {
    const text = refinement.trim();
    if (!text) return;

    if (composerMode === 'edit') {
      submit(text);
    } else {
      refine(text);
    }
    setComposerOpen(false);
    setRefinement('');
    setComposerMode('refine');
  }, [refinement, composerMode, submit, refine]);

  useEffect(() => {
    const seed = initialQuery?.trim();
    if (!seed) return;
    if (seed === lastAutoSubmittedQueryRef.current) return;
    lastAutoSubmittedQueryRef.current = seed;
    const rafId = window.requestAnimationFrame(() => {
      setDraft(seed);
      submit(seed);
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [initialQuery, submit]);

  if (!hasResults && !isHydrating) {
    return (
      <Container className="max-w-6xl">
        <div className="pt-1 pb-3 sm:pt-1 sm:pb-5">
          <SearchHero
            value={draft}
            onChange={setDraft}
            onSubmit={() => submit(draft)}
            onPickSuggestion={(s) => {
              setDraft(s);
              submit(s);
            }}
            filterGroups={filterGroups}
            onFilterGroupsChange={setFilterGroups}
            attachedResume={attachedResume}
            onAddResume={handleAddResumeClick}
            onRemoveResume={() => setAttachedResume(null)}
          />
        </div>
        <ResumeIntakeDialog
          open={resumeDialogOpen}
          onOpenChange={setResumeDialogOpen}
          title={isAuthenticated ? 'Attach Resume to Search' : 'Upload Resume for Search'}
          eyebrow="Search Context"
          description={isAuthenticated
            ? 'Select or upload a resume to enrich your search results.'
            : 'Upload your resume to tailor search results to your background.'}
          onUpload={handleResumeUpload}
          onSelectExisting={isAuthenticated ? handleResumeSelectExisting : undefined}
          anonymousMode={!isAuthenticated}
        />
      </Container>
    );
  }

  return (
    <Container className="max-w-6xl">
      <div className="pb-6 pt-3 sm:pt-4">
        <ResultsFeed
          rounds={rounds}
          onEditQuery={handleEditQuery}
          onDeleteRound={handleDeleteRound}
          onNewSearch={reset}
          pagers={mockPagers}
          resultFilter={resultFilter}
          onResultFilterChange={setResultFilter}
          isLoading={isHydrating}
          bottomSlot={rounds.length > 0 && !isSearching ? (
            <div ref={inlineRefineRef} className="p-2 sm:p-3">
              <SearchComposer
                value={refinement}
                onChange={setRefinement}
                onSubmit={() => { if (refinement.trim()) refine(refinement); }}
                filterGroups={filterGroups}
                onFilterGroupsChange={setFilterGroups}
                attachedResume={attachedResume}
                onAddResume={handleAddResumeClick}
                onRemoveResume={() => setAttachedResume(null)}
                variant="sticky"
                className="shadow-none"
                placeholder="Refine your search or ask a question…"
              />
            </div>
          ) : undefined}
        />

        <div ref={bottomRef} />
      </div>

      {/* Floating bottom refinement composer — hidden when inline refine is in viewport */}
      {!inlineRefineVisible && <FloatingComposer
        value={refinement}
        onChange={setRefinement}
        onSubmit={handleComposerSubmit}
        filterGroups={filterGroups}
        onFilterGroupsChange={setFilterGroups}
        expanded={composerOpen}
        onExpand={() => {
          setRefinement('');
          setComposerMode('refine');
          setComposerOpen(true);
        }}
        onCollapse={() => setComposerOpen(false)}
        attachedResume={attachedResume}
        onAddResume={handleAddResumeClick}
        onRemoveResume={() => setAttachedResume(null)}
      />}

      <ResumeIntakeDialog
        open={resumeDialogOpen}
        onOpenChange={setResumeDialogOpen}
        title={isAuthenticated ? 'Add Resume to Search' : 'Upload Resume for Search'}
        eyebrow="Search Context"
        description={isAuthenticated
          ? 'Select or upload a resume to enrich your search results.'
          : 'Upload your resume to tailor search results to your background.'}
        onUpload={handleResumeUpload}
        onSelectExisting={isAuthenticated ? handleResumeSelectExisting : undefined}
        anonymousMode={!isAuthenticated}
      />
    </Container>
  );
}

/* ----------------------------- Floating Composer -------------------------- */

function FloatingComposer({
  value,
  onChange,
  onSubmit,
  filterGroups,
  onFilterGroupsChange,
  expanded,
  onExpand,
  onCollapse,
  attachedResume,
  onAddResume,
  onRemoveResume,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  filterGroups: SearchFilterGroup[];
  onFilterGroupsChange: (g: SearchFilterGroup[]) => void;
  expanded: boolean;
  onExpand: () => void;
  onCollapse: () => void;
  attachedResume: AttachedResumeInfo | null;
  onAddResume: () => void;
  onRemoveResume: () => void;
}) {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!expanded) return;
    requestAnimationFrame(() => {
      const ta = wrapperRef.current?.querySelector('textarea');
      ta?.focus();
    });
  }, [expanded]);

  useEffect(() => {
    if (!expanded) return;
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        onCollapse();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [expanded, onCollapse]);

  return (
    <div className="fixed inset-x-0 bottom-4 z-30 pointer-events-none">
      <Container className="max-w-4xl">
        <div ref={wrapperRef} className="pointer-events-auto mx-auto max-w-2xl">
          {expanded ? (
            <div className="rounded-xl border border-border bg-card shadow-lg">
              <SearchComposer
                value={value}
                onChange={onChange}
                onSubmit={onSubmit}
                filterGroups={filterGroups}
                onFilterGroupsChange={onFilterGroupsChange}
                attachedResume={attachedResume}
                onAddResume={onAddResume}
                onRemoveResume={onRemoveResume}
                variant="sticky"
                placeholder="Refine your search or ask a question…"
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={onExpand}
              className="flex w-full items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 text-sm text-muted-foreground/60 shadow-lg transition-colors hover:border-primary/40 hover:text-muted-foreground"
            >
              {attachedResume && (
                <FileText className="h-3.5 w-3.5 text-primary/70 flex-shrink-0" />
              )}
              Refine your search or ask a question…
            </button>
          )}
        </div>
      </Container>
    </div>
  );
}
